import { z } from 'zod';
import { AdminReason } from './fields.js';
import { EarlyBirdCounterDto, FreePlanCardDto } from './grand-mock.js';
/** Website checkout (Razorpay) and admin commerce contracts (M7, ADR 0019). */
const productKey = z.string().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/);
const planKey = z.string().regex(/^[a-z0-9_]{1,64}$/);
const examKey = z.string().regex(/^[a-z0-9_]{1,32}$/);
/** A live offer as shown to buyers (ADR 0028): label and end (ISO, with offset). */
/** `endsAt` is null for a capped offer (first N purchases, ADR 0032): it has no end date. */
export const OfferDto = z.object({ label: z.string(), endsAt: z.string().nullable() });
export const WebPlanDto = z.object({
    key: z.string(),
    name: z.string(),
    kind: z.string(),
    /** Rupees charged today: the offer price while an offer runs, else the list price. */
    inr: z.number(),
    /** List price (rupees); greater than `inr` only while an offer runs. */
    listInr: z.number(),
    offer: OfferDto.nullable(),
    /** AI doubt credits this plan includes (ADR 0036); null when it has none. */
    doubtCredits: z.number().nullable(),
    scope: z.enum(['one', 'all']),
    validity: z.string(),
    /** Days the plan lasts; for "until your exam" plans, the longest it can last. */
    validityDays: z.number(),
    /** Lasts until the learner's exam (+ a grace period), so its length varies. */
    untilExam: z.boolean(),
    /** `best_value` from the exam pack (display only). */
    highlight: z.enum(['best_value']).nullable(),
    grants: z.array(z.object({ type: z.string(), qty: z.number().nullable() })),
});
export const WebPlansDto = z.object({
    product: z.object({ key: z.string(), name: z.string(), disclaimer: z.string().nullable() }),
    /** Search-engine title and description for the product page (pack `web.seo`). */
    seo: z.object({ title: z.string().nullable(), description: z.string().nullable() }).nullable(),
    exams: z.array(z.object({ key: z.string(), name: z.string() })),
    plans: z.array(WebPlanDto),
    /** The Free plan card; show it first. */
    freePlan: FreePlanCardDto,
    /** "X of 50 early-bird spots left"; null when the product has no cap. */
    earlyBird: EarlyBirdCounterDto.nullable(),
    checkoutEnabled: z.boolean(),
    legal: z.object({
        entity: z.string(),
        supportEmail: z.string(),
        refundHours: z.number(),
        refundPercent: z.number(),
    }),
    botUsername: z.string().nullable(),
});
export const WebCheckoutRequest = z
    .object({
    productKey,
    planKey,
    examKey: examKey.optional(),
    /** Indian mobile: 10 digits starting 6–9 (with or without +91). */
    phone: z
        .string()
        .trim()
        .transform((v) => v.replace(/[\s-]/g, '').replace(/^(\+?91)(?=\d{10}$)/, ''))
        .pipe(z.string().regex(/^[6-9]\d{9}$/, 'Enter a 10-digit Indian mobile number')),
    email: z.email().max(254).optional(),
    /** The buyer accepted the terms and refund policy. */
    accept: z.literal(true),
    turnstileToken: z.string().max(4096).optional(),
})
    .strict();
export const WebCheckoutDto = z.object({
    orderId: z.string(),
    accessToken: z.string(),
    razorpayOrderId: z.string(),
    keyId: z.string(),
    amount: z.number(),
    currency: z.literal('INR'),
    planName: z.string(),
});
export const WebVerifyRequest = z
    .object({
    orderId: z.uuid(),
    token: z.string().min(16).max(128),
    razorpayPaymentId: z.string().min(1).max(64),
    razorpaySignature: z.string().regex(/^[a-f0-9]{64}$/),
})
    .strict();
export const WebOrderQuery = z.object({ token: z.string().min(16).max(128) }).strict();
export const WebOrderStatusDto = z.object({
    status: z.string(),
    planKey: z.string(),
    planName: z.string(),
    linkCode: z.string().nullable(),
    linked: z.boolean(),
    /** Rupees charged; `listInr` is the struck-through list price when an offer applied. */
    amountInr: z.number(),
    listInr: z.number().nullable(),
    offer: OfferDto.nullable(),
});
export const AdminOrdersQuery = z
    .object({
    status: z
        .enum([
        'pending',
        'authorizing',
        'paid',
        'fulfilled',
        'expired',
        'failed',
        'refund_pending',
        'refunded',
        'refund_failed',
    ])
        .optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
})
    .strict();
export const AdminRefundRequest = z
    .object({
    paymentId: z.uuid(),
    reason: AdminReason,
    /** Skip the 48 h / 20 % policy (technical failure on our side). */
    force: z.boolean().optional(),
})
    .strict();
export const AdminGrantRequest = z
    .object({
    userId: z.uuid(),
    planKey,
    examKey: examKey.optional(),
    reason: AdminReason,
})
    .strict();
export const AdminLookupQuery = z.object({ userId: z.uuid() }).strict();
/**
 * What one plan would add for a signed-in Telegram learner (ADR 0039), shown on the Mini App
 * plans card and in the bot's /upgrade list. Null when there is nothing to say (no exam picked).
 * - `included`: adds no Pro time; `byAll` = covered by an all-exam pass ("Included in your Annual").
 * - `subscribed`: the same plan already renews as a Stars subscription.
 * - `extends`: adds `days` after `after` (that exam's current cover ends).
 * - `new`: starts now; `leftover` lists per-exam passes whose remaining days are not added.
 * The website never gets this (a typed phone number is unverified).
 */
export const PlanCoverageDto = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('included'),
        until: z.string(),
        byAll: z.boolean(),
        /** Name of the pass that covers it, e.g. "All-SSC Annual". */
        byName: z.string(),
    }),
    z.object({ status: z.literal('subscribed'), until: z.string(), byName: z.string() }),
    z.object({
        status: z.literal('extends'),
        after: z.string(),
        until: z.string(),
        days: z.number(),
    }),
    z.object({
        status: z.literal('new'),
        leftover: z.array(z.object({ examName: z.string(), until: z.string(), subscription: z.boolean() })),
    }),
]);
//# sourceMappingURL=commerce.js.map